/**
 * Turn chatbot backend / Dify failures into a readable reason for the
 * "coach unavailable" message shown in the chat window.
 */

// Reasons longer than this are cut so the chat bubble stays readable.
export const MAX_ERROR_REASON_LENGTH = 200;

// OpenRouter answers 402 "Insufficient credits" (or "Key limit exceeded") when
// the API key has no credit left; Dify relays that text in its error message.
const CREDITS_EXHAUSTED_PATTERN =
  /insufficient[ _-]?(credits?|quota|balance|funds)|credits?[ _-]?(exhausted|depleted)|out of credits?|no credits? (left|remaining)|payment required|(error code|status(?: code)?|code|http)\s*:?\s*402\b|key limit exceeded|spending limit|quota (exceeded|exhausted)/i;

export const isCreditsExhaustedError = (message?: string | null): boolean =>
  !!message && CREDITS_EXHAUSTED_PATTERN.test(message);

const pickMessage = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return (
      pickMessage(obj.message) ||
      pickMessage(obj.error) ||
      pickMessage(obj.detail) ||
      pickMessage(obj.statusText) ||
      pickMessage(obj.code)
    );
  }
  return undefined;
};

/**
 * Extract a readable error message from whatever the chatbot backend sent:
 * a JSON body ({ message } / { error } / { error: { message } } / { detail }),
 * a plain text body, an Error, a Dify SSE error event or an axios rejection.
 */
export const extractErrorMessage = (payload: unknown): string | undefined => {
  if (typeof payload === 'string') {
    const text = payload.trim();
    if (!text) return undefined;
    // The backend may answer with a JSON body even on error
    if (text.startsWith('{') || text.startsWith('[')) {
      try {
        return pickMessage(JSON.parse(text)) || text;
      } catch {
        return text;
      }
    }
    // Never dump an HTML error page (reverse-proxy 502...) into the chat
    if (/^<!doctype html|^<html/i.test(text)) return undefined;
    return text;
  }
  return pickMessage(payload);
};

export const truncateReason = (reason: string): string =>
  reason.length > MAX_ERROR_REASON_LENGTH
    ? `${reason.slice(0, MAX_ERROR_REASON_LENGTH - 1).trimEnd()}…`
    : reason;
